import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import type { CilDecisionOutcome, CilRulingCoverage } from "@hp/types";
import { Badge, Button, Card, CardBody } from "@hp/ui";
import { fetchCilCoverage } from "../../api/cil";
import { EmptyState, ErrorState, LoadingState } from "../../components/Feedback";
import { HoverHint } from "../../components/HoverHint";
import { PartyName } from "../../components/cil/DocumentLinks";
import { PageHeader } from "../../components/layout/PageHeader";
import { SourceDocumentViewer } from "../../components/SourceDocumentViewer";
import { formatDate } from "../../lib/format";

type Filter = CilRulingCoverage["status"];

const FILTERS: Array<{ key: Filter; label: string; hint: string }> = [
  { key: "raised", label: "Raised an event", hint: "Rulings that raised at least one changed event (open or closed). Open the event from the row." },
  {
    key: "waiting",
    label: "Waiting for review",
    hint: "Nothing raised yet because some of its readings are waiting for a person to approve them (or to name the cost). Until then they are not compared with HP.",
  },
  { key: "no_effect", label: "No effect on HP", hint: "Every decision in these rulings was compared with HP's orders and contracts; each row says why it changes nothing for HP." },
  { key: "hp_order", label: "HP's own orders", hint: "HP's orders: their decisions make up HP's position on each cost, which every other ruling is compared with." },
];

const KIND: Record<CilDecisionOutcome["kind"], { label: string; tone: "success" | "info" | "neutral" | "brand" }> = {
  event: { label: "Changed event", tone: "success" },
  covered: { label: "Covered", tone: "info" },
  hp_position: { label: "HP's position", tone: "brand" },
  no_effect: { label: "No effect", tone: "neutral" },
};

const SLUG = { disallowed_to_allowed: "cil-disallowed-to-allowed", allowed_to_disallowed: "cil-allowed-to-disallowed" } as const;

const TERRITORY: Record<string, string> = { MP: "Madhya Pradesh", CENTRAL: "CERC contracts", UP: "Uttar Pradesh", KA: "Karnataka" };

function forumName(name: string): string {
  return name === "SC" ? "Supreme Court" : name;
}

function Outcome({ outcome }: { outcome: CilDecisionOutcome }) {
  const kind = KIND[outcome.kind];
  return (
    <li className="flex flex-wrap items-start gap-x-2 gap-y-1 text-sm">
      <Badge tone={kind.tone}>{kind.label}</Badge>
      <span className="font-medium text-zinc-900">{outcome.cost ?? "No cost named"}</span>
      <span className="text-zinc-500">({outcome.result})</span>
      {outcome.periods.length ? (
        <span className="text-xs text-zinc-500">
          {outcome.periods.join(", ")}
          {outcome.decision_count > outcome.periods.length ? ` · ${outcome.decision_count} decisions` : ""}
        </span>
      ) : null}
      <span className="basis-full text-zinc-700 sm:basis-auto">{outcome.reason}</span>
      {outcome.change_event_ids.map((id, i) => (
        <Link
          key={id}
          to={`/popup/${SLUG[outcome.change_event_directions[i] ?? "disallowed_to_allowed"]}/${id}`}
          className="text-xs font-medium text-[var(--color-accent-content)] hover:underline"
        >
          Open {outcome.change_event_directions[i] === "allowed_to_disallowed" ? "risk" : "opportunity"}
          {outcome.change_event_territories[i] ? ` (${TERRITORY[outcome.change_event_territories[i]] ?? outcome.change_event_territories[i]})` : ""} →
        </Link>
      ))}
    </li>
  );
}

function RulingRow({ row, onOpen }: { row: CilRulingCoverage; onOpen: (row: CilRulingCoverage) => void }) {
  const [open, setOpen] = React.useState(row.status !== "hp_order");
  const shown = row.outcomes.filter((o) => o.kind !== "hp_position");
  return (
    <li className="flex flex-col gap-2 border-b border-zinc-100 py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-zinc-900">
          {forumName(row.forum_name)} {row.case_ref ?? ""}
        </span>
        <span className="text-sm text-zinc-500">{row.order_date ? formatDate(row.order_date) : "Undated"}</span>
        {row.parties.length ? (
          <span className="text-sm text-zinc-600">
            For <PartyName>{row.parties.join(", ")}</PartyName>
          </span>
        ) : null}
        {row.open_events ? (
          <Badge tone="success">
            {row.open_events} open event{row.open_events === 1 ? "" : "s"}
            {row.counted_events !== row.open_events ? ` (${row.counted_events} in the card's number)` : ""}
          </Badge>
        ) : null}
        {row.waiting_review ? (
          <HoverHint hint="Readings of this ruling waiting for a person to approve them in the review queue (Workbench). They are not compared with HP until approved.">
            <Badge tone="warning">
              {row.waiting_review} reading{row.waiting_review === 1 ? "" : "s"} waiting for review
              {row.unnamed_costs ? ` · ${row.unnamed_costs} with an unnamed cost` : ""}
            </Badge>
          </HoverHint>
        ) : null}
        <div className="ml-auto flex items-center gap-1">
          {row.open_decision_id ? (
            <Button size="sm" variant="ghost" onClick={() => onOpen(row)}>
              <FileText className="h-4 w-4" aria-hidden /> Open order
            </Button>
          ) : null}
          {shown.length ? (
            <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
              {open ? "Hide decisions" : `Show ${shown.length} decision${shown.length === 1 ? "" : "s"}`}
            </Button>
          ) : null}
        </div>
      </div>
      {open && shown.length ? (
        <ul className="flex flex-col gap-2 pl-1">
          {shown.map((o) => (
            <Outcome key={`${o.decision_id}-${o.kind}`} outcome={o} />
          ))}
        </ul>
      ) : null}
      {row.status === "hp_order" && !shown.length ? (
        <p className="text-sm text-zinc-600">
          {row.outcomes.length} approved decision{row.outcomes.length === 1 ? "" : "s"} form HP's position; every other ruling is compared with them.
        </p>
      ) : null}
    </li>
  );
}

/**
 * Every ruling read, and what became of it (GET /cil/coverage): the changed
 * events it raised, or - decision by decision - why it changes nothing for
 * HP, and how many of its readings still wait for a person. Nothing is left
 * unaccounted for.
 */
export function CilCoveragePage() {
  const [params, setParams] = useSearchParams();
  const [openRow, setOpenRow] = React.useState<CilRulingCoverage | null>(null);
  const query = useQuery({ queryKey: ["cil", "coverage"], queryFn: fetchCilCoverage });
  const rows = query.data ?? [];
  const asked = params.get("show") as Filter | null;
  const filter: Filter = asked && FILTERS.some((f) => f.key === asked) ? asked : (FILTERS.find((f) => rows.some((r) => r.status === f.key))?.key ?? "raised");
  const shown = rows.filter((r) => r.status === filter);
  const waitingReadings = rows.reduce((n, r) => n + r.waiting_review, 0);
  return (
    <div className="page-stack">
      <div>
        <Link to="/popup/cil-disallowed-to-allowed" className="text-xs font-medium text-[var(--color-accent-content)] hover:underline">
          ← Back to Changed events from Disallowed
        </Link>
      </div>
      <PageHeader
        title="Every ruling checked"
        description="Each order and judgment read, and what became of it: the changed events it raised, or why it changes nothing for HP. Readings waiting for a person are shown so none is left unaddressed."
      />
      {query.isSuccess ? (
        <p className="text-sm text-zinc-700">
          {rows.length} rulings read · {rows.filter((r) => r.status === "raised").length} raised an event ·{" "}
          {rows.filter((r) => r.status === "no_effect").length} have no effect on HP ·{" "}
          <span className={waitingReadings ? "font-semibold text-amber-800" : ""}>
            {waitingReadings} reading{waitingReadings === 1 ? "" : "s"} waiting for review
          </span>
        </p>
      ) : null}
      <div role="tablist" aria-label="Which rulings" className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <HoverHint key={f.key} hint={f.hint} delayMs={1000}>
            <button
              role="tab"
              aria-selected={filter === f.key}
              onClick={() => setParams({ show: f.key }, { replace: true })}
              className={[
                "rounded-md px-3 py-1.5 text-sm font-medium",
                filter === f.key ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200",
              ].join(" ")}
            >
              {f.label}
              {query.data ? (
                <span className={["ml-1.5 tabular-nums", filter === f.key ? "text-zinc-300" : "text-zinc-500"].join(" ")}>
                  {rows.filter((r) => r.status === f.key).length}
                </span>
              ) : null}
            </button>
          </HoverHint>
        ))}
      </div>
      {query.isPending ? <LoadingState label="Checking every ruling..." /> : null}
      {query.isError ? <ErrorState error={query.error} label="Could not load the rulings" /> : null}
      {query.isSuccess && !shown.length ? <EmptyState>Nothing here.</EmptyState> : null}
      {shown.length ? (
        <Card>
          <CardBody>
            <ul className="flex flex-col">
              {shown.map((row) => (
                <RulingRow key={row.ruling_id} row={row} onOpen={setOpenRow} />
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
      {openRow?.open_decision_id ? (
        <SourceDocumentViewer
          source={{ type: "decision", id: openRow.open_decision_id, label: `${forumName(openRow.forum_name)} ${openRow.case_ref ?? ""}`.trim() }}
          onClose={() => setOpenRow(null)}
        />
      ) : null}
    </div>
  );
}
